import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { MailMinus, RotateCw, Search, ShieldOff, Plus, Loader2, Inbox, ScrollText, ShieldCheck, UserCog, Cpu, Download, TrendingUp, TrendingDown, Activity, Minus, Bell, BellOff, Clock, AlertOctagon, Flag } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from 'recharts';
import api from '../lib/api';
import { toast } from 'sonner';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

/**
 * EmailSuppressionsAdminPage
 * --------------------------
 * Super-admin-only view of the global email suppression list.
 * The same list is honoured by:
 *   • Comparative-digest schedule sends
 *   • Prospectus public-share "Send to CFO" emails
 *
 * Lets ops:
 *   • Browse active suppressions (with search)
 *   • Toggle inclusion of dismissed (restored) rows
 *   • Restore a single email so future sends go through
 *   • Manually add a suppression from a verbal/ticket request
 */
const EmailSuppressionsAdminPage = () => {
  const [data, setData] = useState({ rows: [], active_total: 0, dismissed_total: 0 });
  const [loading, setLoading] = useState(true);
  const [includeDismissed, setIncludeDismissed] = useState(false);
  const [search, setSearch] = useState('');
  const [restoring, setRestoring] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newReason, setNewReason] = useState('manual');
  const [newNote, setNewNote] = useState('');
  const [adding, setAdding] = useState(false);
  const [tab, setTab] = useState('list'); // 'list' | 'audit' | 'bounces'
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [trend, setTrend] = useState(null);
  const [bounces, setBounces] = useState([]);
  const [bouncesLoading, setBouncesLoading] = useState(false);

  const fetchTrend = useCallback(async () => {
    try {
      const { data: res } = await api.get('/admin/email-suppressions/deliverability-trend', {
        params: { days: 14 },
      });
      setTrend(res || null);
    } catch {
      // Non-critical — sparkline silently hides if it fails.
    }
  }, []);

  useEffect(() => { fetchTrend(); }, [fetchTrend]);

  const fetchSuppressions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (includeDismissed) params.include_dismissed = true;
      if (search.trim()) params.search = search.trim();
      const { data: res } = await api.get('/admin/email-suppressions', { params });
      setData(res || { rows: [], active_total: 0, dismissed_total: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load suppressions');
    } finally {
      setLoading(false);
    }
  }, [includeDismissed, search]);

  useEffect(() => { fetchSuppressions(); }, [fetchSuppressions]);

  const fetchAuditLog = useCallback(async () => {
    setAuditLoading(true);
    try {
      const { data: res } = await api.get('/admin/email-suppressions/audit-log', {
        params: { limit: 200 },
      });
      setAuditEvents(res?.events || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load audit log');
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'audit') fetchAuditLog();
  }, [tab, fetchAuditLog]);

  const fetchBounces = useCallback(async () => {
    setBouncesLoading(true);
    try {
      const { data: res } = await api.get('/admin/email-bounces', {
        params: { days: 14, limit: 200 },
      });
      setBounces(res?.events || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load bounce events');
    } finally {
      setBouncesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'bounces') fetchBounces();
  }, [tab, fetchBounces]);

  const exportAuditCsv = async () => {
    try {
      // Pull the full set (server cap is 500) for the evidence pack.
      const { data: res } = await api.get('/admin/email-suppressions/audit-log', {
        params: { limit: 500 },
      });
      const events = res?.events || [];
      if (events.length === 0) {
        toast.info('Nothing to export — audit log is empty.');
        return;
      }
      const escape = (v) => {
        const s = (v ?? '').toString();
        if (s.includes('"') || s.includes(',') || s.includes('\n')) {
          return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
      };
      const header = ['action', 'email', 'actor_name', 'actor_email', 'actor_kind', 'reason', 'at', 'event_id', 'source_row_id'];
      const lines = [header.join(',')];
      for (const e of events) {
        lines.push([
          e.kind, e.email, e.actor_name, e.actor_email,
          e.actor_kind, e.reason, e.at, e.event_id, e.source_row_id,
        ].map(escape).join(','));
      }
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.href = url;
      a.download = `email-suppressions-audit-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${events.length} event${events.length === 1 ? '' : 's'} to CSV`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to export CSV');
    }
  };

  const restoreOne = async (email) => {
    setRestoring(email);
    try {
      const { data: res } = await api.post(
        `/admin/email-suppressions/${encodeURIComponent(email)}/restore`
      );
      toast.success(`Restored ${email} (${res.rows_updated} row${res.rows_updated === 1 ? '' : 's'})`);
      await fetchSuppressions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to restore');
    } finally {
      setRestoring(null);
    }
  };

  const addManual = async () => {
    if (!newEmail.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setAdding(true);
    try {
      const { data: res } = await api.post('/admin/email-suppressions', {
        email: newEmail.trim().toLowerCase(),
        reason: newReason.trim() || 'manual',
        note: newNote.trim() || null,
      });
      if (res?.already_suppressed) {
        toast.info(`${res.email} is already on the suppression list.`);
      } else {
        toast.success(`Suppressed ${res.email}`);
      }
      setAddOpen(false);
      setNewEmail(''); setNewReason('manual'); setNewNote('');
      await fetchSuppressions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add suppression');
    } finally {
      setAdding(false);
    }
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const __pageTheme = getLightSectionTheme('Navy');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Email Suppressions"
        description="Manage bounced and suppressed email addresses, monitor channel health, and unblock genuine deliverability issues."
        theme={__pageTheme}
        icon={MailMinus}
        testId="email-suppressions-hero"
      />
    )}>
    <div className="px-4 md:px-8 lg:px-12 py-6 space-y-6" data-testid="email-suppressions-admin-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={fetchSuppressions}
            disabled={loading}
            data-testid="suppressions-refresh-btn"
          >
            <RotateCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => setAddOpen(true)}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="suppressions-add-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add suppression
          </Button>
        </div>
      </div>

      {trend && (
        <Card data-testid="suppressions-trend-card" className="border-slate-200">
          <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="w-4 h-4 text-indigo-600" />
                Deliverability trend (last 14 days)
              </CardTitle>
              <CardDescription className="mt-1">
                Daily new-suppression counts. A rising trend often means a sender-reputation issue worth investigating.
              </CardDescription>
            </div>
            <div className="flex flex-col items-end gap-1.5">
              <TrendStatusBadge trend={trend} />
              <AlertStatePill alertState={trend.alert_state} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-center">
              <div className="space-y-2 lg:col-span-1">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Last 7 days</p>
                  <p className="text-3xl font-bold text-slate-900" data-testid="suppressions-trend-last7">
                    {trend.last_7_total}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Previous 7 days: <span className="text-slate-700 font-semibold">{trend.previous_7_total}</span></p>
                  {trend.delta_pct !== null && (
                    <p className={`text-xs font-semibold ${
                      trend.status === 'degrading' ? 'text-rose-700' :
                      trend.status === 'improving' ? 'text-emerald-700' :
                      'text-slate-600'
                    }`}>
                      {trend.delta_pct >= 0 ? '+' : ''}{trend.delta_pct}% week-over-week
                    </p>
                  )}
                </div>
              </div>
              <div className="lg:col-span-2 h-28" data-testid="suppressions-trend-sparkline">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trend.series} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="suppGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#e11d48" stopOpacity={0.32} />
                        <stop offset="100%" stopColor="#e11d48" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: '#94a3b8' }}
                      tickFormatter={(d) => d ? d.slice(5) : ''}
                      interval="preserveStartEnd"
                    />
                    <YAxis hide allowDecimals={false} />
                    <Tooltip
                      formatter={(v, _n, p) => [`${v} new`, 'Suppressions']}
                      labelFormatter={(d) => `Day ${d}`}
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                    />
                    <Area type="monotone" dataKey="count" stroke="#e11d48" strokeWidth={2} fill="url(#suppGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card data-testid="suppressions-active-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-600 flex items-center gap-2"><MailMinus className="w-4 h-4" /> Active</CardTitle></CardHeader>
          <CardContent><div className="text-3xl font-bold text-rose-700">{data.active_total}</div></CardContent>
        </Card>
        <Card data-testid="suppressions-dismissed-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-600 flex items-center gap-2"><Inbox className="w-4 h-4" /> Restored</CardTitle></CardHeader>
          <CardContent><div className="text-3xl font-bold text-slate-700">{data.dismissed_total}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-600">Showing</CardTitle></CardHeader>
          <CardContent><div className="text-3xl font-bold text-slate-900">{tab === 'audit' ? auditEvents.length : tab === 'bounces' ? bounces.length : data.rows.length}</div></CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-2 border-b border-slate-200" data-testid="suppressions-tabs">
        <button
          onClick={() => setTab('list')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${tab === 'list' ? 'border-rose-600 text-rose-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
          data-testid="suppressions-tab-list"
        >
          <ShieldOff className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Suppressions
        </button>
        <button
          onClick={() => setTab('audit')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${tab === 'audit' ? 'border-rose-600 text-rose-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
          data-testid="suppressions-tab-audit"
        >
          <ScrollText className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Audit log
        </button>
        <button
          onClick={() => setTab('bounces')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${tab === 'bounces' ? 'border-rose-600 text-rose-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
          data-testid="suppressions-tab-bounces"
        >
          <AlertOctagon className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Bounces
        </button>
      </div>

      {tab === 'list' && (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Filter by email substring…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="suppressions-search"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 select-none">
              <input
                type="checkbox"
                checked={includeDismissed}
                onChange={(e) => setIncludeDismissed(e.target.checked)}
                data-testid="suppressions-include-dismissed"
              />
              Include restored
            </label>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
          ) : data.rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="suppressions-empty">
              No suppressions match your filter.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="suppressions-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left font-semibold text-slate-700 p-3">Email</th>
                    <th className="text-left font-semibold text-slate-700 p-3">Reason</th>
                    <th className="text-left font-semibold text-slate-700 p-3">Suppressed</th>
                    <th className="text-left font-semibold text-slate-700 p-3">Status</th>
                    <th className="text-right font-semibold text-slate-700 p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => {
                    const isDismissed = !!r.dismissed_at;
                    return (
                      <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="p-3 font-mono text-xs text-slate-900 break-all">{r.email}</td>
                        <td className="p-3 text-slate-700">{r.reason || '—'}</td>
                        <td className="p-3 text-slate-500 whitespace-nowrap">{fmt(r.suppressed_at)}</td>
                        <td className="p-3">
                          {isDismissed ? (
                            <Badge className="bg-slate-100 text-slate-700">Restored</Badge>
                          ) : (
                            <Badge className="bg-rose-100 text-rose-800">Active</Badge>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          {!isDismissed ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => restoreOne(r.email)}
                              disabled={restoring === r.email}
                              data-testid={`suppressions-restore-${r.id}`}
                            >
                              {restoring === r.email ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Restore'}
                            </Button>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {tab === 'audit' && (
        <Card data-testid="suppressions-audit-card">
          <CardHeader className="pb-3 flex flex-row items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <ScrollText className="w-4 h-4 text-slate-600" />
                Recent suppression-list actions
              </CardTitle>
              <CardDescription className="mt-1">
                Compliance trail: every "row created" and "row restored" event,
                with the responsible actor. Auto-bounce events are credited to
                the bounce engine.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={exportAuditCsv}
                disabled={auditLoading || auditEvents.length === 0}
                data-testid="suppressions-audit-export-csv"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                Export CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchAuditLog}
                disabled={auditLoading}
                data-testid="suppressions-audit-refresh"
              >
                <RotateCw className={`w-3.5 h-3.5 mr-1.5 ${auditLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {auditLoading ? (
              <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
            ) : auditEvents.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500" data-testid="suppressions-audit-empty">
                No audit events yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="suppressions-audit-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left font-semibold text-slate-700 p-3">Action</th>
                      <th className="text-left font-semibold text-slate-700 p-3">Email</th>
                      <th className="text-left font-semibold text-slate-700 p-3">Actor</th>
                      <th className="text-left font-semibold text-slate-700 p-3">Reason</th>
                      <th className="text-left font-semibold text-slate-700 p-3">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditEvents.map((e) => {
                      const isRestored = e.kind === 'restored';
                      const ActorIcon = e.actor_kind === 'system' ? Cpu : (isRestored ? ShieldCheck : UserCog);
                      return (
                        <tr key={e.event_id} className="border-b border-slate-100 hover:bg-slate-50/50">
                          <td className="p-3 whitespace-nowrap">
                            {isRestored ? (
                              <Badge className="bg-emerald-100 text-emerald-800">
                                <ShieldCheck className="w-3 h-3 mr-1" /> Restored
                              </Badge>
                            ) : (
                              <Badge className="bg-rose-100 text-rose-800">
                                <ShieldOff className="w-3 h-3 mr-1" /> Suppressed
                              </Badge>
                            )}
                          </td>
                          <td className="p-3 font-mono text-xs text-slate-900 break-all">{e.email}</td>
                          <td className="p-3 text-slate-700">
                            <span className="inline-flex items-center gap-1.5">
                              <ActorIcon className="w-3.5 h-3.5 text-slate-400" />
                              <span className="font-medium">{e.actor_name}</span>
                              {e.actor_email && (
                                <span className="text-xs text-slate-500">({e.actor_email})</span>
                              )}
                            </span>
                          </td>
                          <td className="p-3 text-slate-600">{e.reason || '—'}</td>
                          <td className="p-3 text-slate-500 whitespace-nowrap">{fmt(e.at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'bounces' && (
        <Card data-testid="suppressions-bounces-card">
          <CardHeader className="pb-3 flex flex-row items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <AlertOctagon className="w-4 h-4 text-rose-600" />
                Raw bounce events (last 14 days)
              </CardTitle>
              <CardDescription className="mt-1">
                Provider raw error messages from Resend webhooks. Use these
                to understand <em>why</em> an address was suppressed — bounce
                type, subtype, and the original recipient context.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchBounces}
              disabled={bouncesLoading}
              data-testid="suppressions-bounces-refresh"
            >
              <RotateCw className={`w-3.5 h-3.5 mr-1.5 ${bouncesLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {bouncesLoading ? (
              <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
            ) : bounces.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500" data-testid="suppressions-bounces-empty">
                No bounce or complaint events in the last 14 days. Your sender reputation looks clean.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="suppressions-bounces-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left font-semibold text-slate-700 p-3">Type</th>
                      <th className="text-left font-semibold text-slate-700 p-3">Email</th>
                      <th className="text-left font-semibold text-slate-700 p-3">Bounce kind</th>
                      <th className="text-left font-semibold text-slate-700 p-3">Provider message</th>
                      <th className="text-left font-semibold text-slate-700 p-3">Subject</th>
                      <th className="text-left font-semibold text-slate-700 p-3">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bounces.map((b) => (
                      <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="p-3 whitespace-nowrap">
                          {b.status === 'complained' ? (
                            <Badge className="bg-amber-100 text-amber-800">
                              <Flag className="w-3 h-3 mr-1" /> Complaint
                            </Badge>
                          ) : (
                            <Badge className="bg-rose-100 text-rose-800">
                              <AlertOctagon className="w-3 h-3 mr-1" /> Bounce
                            </Badge>
                          )}
                        </td>
                        <td className="p-3 font-mono text-xs text-slate-900 break-all">{b.email}</td>
                        <td className="p-3 text-slate-700 whitespace-nowrap">
                          {b.bounce_type ? (
                            <span>
                              <span className="font-semibold">{b.bounce_type}</span>
                              {b.bounce_subtype && (
                                <span className="text-slate-400"> · {b.bounce_subtype}</span>
                              )}
                            </span>
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="p-3 text-xs text-slate-600 max-w-md break-words">
                          {b.message || <span className="text-slate-400">—</span>}
                        </td>
                        <td className="p-3 text-slate-600 max-w-xs truncate" title={b.subject || ''}>
                          {b.subject || <span className="text-slate-400">—</span>}
                        </td>
                        <td className="p-3 text-slate-500 whitespace-nowrap">{fmt(b.at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="suppressions-add-dialog">
          <DialogHeader>
            <DialogTitle>Add manual suppression</DialogTitle>
            <DialogDescription>
              Adds the email to the global suppression list immediately. Useful
              when a stakeholder verbally asks to be removed before any bounce
              has been recorded.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="manual-email" className="text-xs">Email</Label>
              <Input
                id="manual-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="cfo@example.com"
                data-testid="suppressions-add-email"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="manual-reason" className="text-xs">Reason</Label>
              <Input
                id="manual-reason"
                value={newReason}
                onChange={(e) => setNewReason(e.target.value)}
                placeholder="manual / unsubscribe-verbal / spam-complaint"
                data-testid="suppressions-add-reason"
              />
            </div>
            <div>
              <Label htmlFor="manual-note" className="text-xs">Note (optional)</Label>
              <textarea
                id="manual-note"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                rows={3}
                maxLength={300}
                placeholder="Ticket #1234 — CFO asked for removal during onsite review"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                data-testid="suppressions-add-note"
              />
              <p className="text-[10px] text-slate-400 mt-0.5">{newNote.length}/300 chars</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)} data-testid="suppressions-add-cancel">Cancel</Button>
            <Button
              onClick={addManual}
              disabled={adding || !newEmail.includes('@')}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="suppressions-add-confirm"
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add suppression
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PageShell>
  );
};

export default EmailSuppressionsAdminPage;

const TrendStatusBadge = ({ trend }) => {
  if (!trend) return null;
  const map = {
    degrading: { Icon: TrendingUp, cls: 'bg-rose-100 text-rose-800', label: 'Degrading' },
    improving: { Icon: TrendingDown, cls: 'bg-emerald-100 text-emerald-800', label: 'Improving' },
    stable: { Icon: Minus, cls: 'bg-slate-100 text-slate-700', label: 'Stable' },
    quiet: { Icon: Minus, cls: 'bg-slate-100 text-slate-500', label: 'Quiet — no recent activity' },
  };
  const { Icon, cls, label } = map[trend.status] || map.stable;
  return (
    <Badge className={`${cls} font-semibold`} data-testid="suppressions-trend-status">
      <Icon className="w-3 h-3 mr-1" />
      {label}
    </Badge>
  );
};

const _humanRelative = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!t) return null;
  const diffSec = Math.floor((Date.now() - t) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  const days = Math.floor(diffSec / 86400);
  return `${days}d ago`;
};

const AlertStatePill = ({ alertState }) => {
  if (!alertState) return null;
  const lastAlerted = _humanRelative(alertState.last_alerted_at);
  const lastChecked = _humanRelative(alertState.checked_at);

  // No watcher tick recorded yet — likely first deploy.
  if (!lastChecked) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] text-slate-400 font-medium px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200"
        data-testid="suppressions-alert-state-pill"
        title="The Slack watcher hasn't run yet"
      >
        <Clock className="w-3 h-3" />
        Watcher: pending first run
      </span>
    );
  }

  if (lastAlerted) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] text-rose-700 font-medium px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200"
        data-testid="suppressions-alert-state-pill"
        title={`Last alerted at ${new Date(alertState.last_alerted_at).toLocaleString()} · Watcher last ran ${lastChecked}`}
      >
        <Bell className="w-3 h-3" />
        Last alerted {lastAlerted}
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] text-emerald-700 font-medium px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200"
      data-testid="suppressions-alert-state-pill"
      title={`Slack watcher last ran ${lastChecked} · no alerts ever fired`}
    >
      <BellOff className="w-3 h-3" />
      Never alerted · checked {lastChecked}
    </span>
  );
};
